import { Box, FormControl, FormErrorMessage, FormHelperText, FormLabel, Heading, Switch, Text } from "@chakra-ui/react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import type {
  PriorInformationCallDto,
  SourcingEventDto,
  SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { ThemedDatePicker } from "../../../../../common/input/DateTimeSelectors/ThemedDatePicker";
import { PRIOR_INFORMATION_CALL } from "../utils/karens";
import { startOfUtcDay } from "./schemeValidityDates";

interface Props {
  eventId: string;
  call: PriorInformationCallDto | null;
  isEditable: boolean;
}

const earliestInvitation = () => moment().add(PRIOR_INFORMATION_CALL.earliestInvitationDays, "days").startOf("day");
const latestInvitation = () => moment().add(PRIOR_INFORMATION_CALL.latestInvitationMonths, "months").endOf("day");

export const PriorInformationCallField = ({ eventId, call, isEditable }: Props) => {
  const { t } = useTranslation();
  const { updateField } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const planned = call ? moment(call.plannedInvitationAt) : null;
  const outsideWindow =
    planned != null && isEditable && (planned.isBefore(earliestInvitation()) || planned.isAfter(latestInvitation()));

  const toggle = (called: boolean) =>
    updateField({
      publicContext: {
        priorInformationCall: called
          ? { plannedInvitationAt: earliestInvitation().add(7, "days").toISOString() }
          : null,
      },
    });

  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" bg="white" p="5" mb="8">
      <FormControl display="flex" alignItems="flex-start" justifyContent="space-between" gap="6">
        <Box>
          <FormLabel htmlFor="priorInformationCallEnabled" mb="1" cursor="pointer">
            <Heading as="h3" size="sm">
              {t("Announce the competition with a prior information notice")}
            </Heading>
          </FormLabel>
          <Text fontSize="sm" color="smMuted">
            {t(
              "Forsyningsforskriften § 17-2 (2). Suppliers register their interest on the notice, and you invite them to confirm it 35 days to 12 months after the notice is sent.",
            )}
          </Text>
        </Box>
        <Switch
          id="priorInformationCallEnabled"
          isChecked={call != null}
          isDisabled={!isEditable}
          onChange={(e) => toggle(e.target.checked)}
          colorScheme="teal"
          mt="1"
        />
      </FormControl>

      {call && (
        <FormControl mt="5" maxW="sm" isInvalid={outsideWindow}>
          <FormLabel fontSize="sm">{t("Planned invitation to confirm interest")}</FormLabel>
          <ThemedDatePicker
            date={call.plannedInvitationAt}
            isDisabled={!isEditable}
            minDate={earliestInvitation().toDate()}
            onChange={(date) => {
              if (!date) return;
              updateField({ publicContext: { priorInformationCall: { plannedInvitationAt: startOfUtcDay(date) } } });
            }}
          />
          {outsideWindow ? (
            <FormErrorMessage>{t("zz_FieldErrorCode_PlannedInvitationOutsideNoticeWindow")}</FormErrorMessage>
          ) : (
            <FormHelperText>
              {t("The request deadline is set when you send the invitation, at least 15 days after it.")}
            </FormHelperText>
          )}
        </FormControl>
      )}
    </Box>
  );
};
